// Terminal-styled (Catppuccin Mocha) admin viewer for the
// property_document_ocr_logs table. Lists the authenticated user's own OCR
// runs, lets the user keyboard-navigate the list (j/k/g/G/r/?/Esc/Enter),
// and renders the image bytes that were sent + the raw model response
// verbatim. Logs are scoped server-side to actor_user_id so each user only
// sees their own runs — no separate admin role is required.

import { Component, esc } from '../core/component.js';
import { api } from '../core/api.js';

export class OcrLogsView extends Component {
  constructor(ctx = {}) {
    super(ctx);
    this.state = {
      rows: [],
      selectedId: ctx?.query?.id || null,
      detail: null,
      loading: true,
      detailLoading: false,
      error: null,
      helpOpen: false,
    };
    this._onKey = (e) => this._handleKey(e);
  }

  unmount() {
    document.removeEventListener('keydown', this._onKey);
  }

  async _loadList() {
    this.setState({ loading: true, error: null });
    try {
      const res = await api.get('/properties/ocr-logs?limit=100');
      const rows = Array.isArray(res?.data) ? res.data : [];
      const sel = this.state.selectedId && rows.some((r) => r.id === this.state.selectedId)
        ? this.state.selectedId
        : (rows[0]?.id || null);
      this.setState({ rows, loading: false, selectedId: sel });
      if (sel) await this._loadDetail(sel);
    } catch (e) {
      this.setState({ loading: false, error: e.message || 'failed to load' });
    }
  }

  async _loadDetail(id) {
    if (!id) return;
    this.setState({ detailLoading: true });
    try {
      const row = await api.get(`/properties/ocr-logs/${encodeURIComponent(id)}`);
      this.setState({ detail: row, detailLoading: false });
    } catch (e) {
      this.setState({ detail: null, detailLoading: false, error: e.message || 'failed to load row' });
    }
  }

  _select(id) {
    if (id === this.state.selectedId) return;
    this.setState({ selectedId: id });
    this._loadDetail(id);
    // Make sure the cursor row is visible in the scrolling list. CSS.escape
    // isn't available in older jsdom; fall back to a simple attribute scan.
    queueMicrotask(() => {
      const escape = (typeof CSS !== 'undefined' && CSS.escape) ? CSS.escape : null;
      const el = escape
        ? this.el?.querySelector(`[data-row="${escape(id)}"]`)
        : Array.from(this.el?.querySelectorAll('[data-row]') || []).find((n) => n.getAttribute('data-row') === id);
      el?.scrollIntoView?.({ block: 'nearest' });
    });
  }

  _move(delta) {
    const { rows, selectedId } = this.state;
    if (rows.length === 0) return;
    const i = Math.max(0, rows.findIndex((r) => r.id === selectedId));
    const next = Math.max(0, Math.min(rows.length - 1, i + delta));
    this._select(rows[next].id);
  }

  _jumpTo(i) {
    const { rows } = this.state;
    if (rows.length === 0) return;
    const idx = Math.max(0, Math.min(rows.length - 1, i));
    this._select(rows[idx].id);
  }

  _handleKey(e) {
    // Let the user copy text from the raw_response <pre> without hijacking
    // every keystroke. Modifier keys (Ctrl/Cmd/Alt) always pass through.
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    if (this.state.helpOpen && e.key === 'Escape') { e.preventDefault(); this.setState({ helpOpen: false }); return; }
    switch (e.key) {
      case 'j': case 'ArrowDown': e.preventDefault(); this._move(+1); break;
      case 'k': case 'ArrowUp':   e.preventDefault(); this._move(-1); break;
      case 'g': case 'Home':      e.preventDefault(); this._jumpTo(0); break;
      case 'G': case 'End':       e.preventDefault(); this._jumpTo(this.state.rows.length - 1); break;
      case 'r':                   e.preventDefault(); this._loadList(); break;
      case '?':                   e.preventDefault(); this.setState({ helpOpen: !this.state.helpOpen }); break;
      case 'Enter':               e.preventDefault(); if (this.state.selectedId) this._loadDetail(this.state.selectedId); break;
      default: break;
    }
  }

  template() {
    return `<div class="ocrlog-root">
      ${STYLE}
      ${this._headerHtml()}
      <div class="ocrlog-body">
        ${this._listHtml()}
        ${this._detailHtml()}
      </div>
      ${this._footerHtml()}
      ${this.state.helpOpen ? this._helpHtml() : ''}
    </div>`;
  }

  mount() {
    // Re-attach the keydown listener on each re-render (Component.render
    // replaces this.el so we re-bind to be safe). Idempotent because
    // _onKey is the same callback reference.
    document.addEventListener('keydown', this._onKey);
    this._mountClicks();
    // Kick off the initial fetch exactly once — subsequent re-renders must
    // not re-enter _loadList, which itself calls setState and would loop.
    if (!this._bootstrapped) {
      this._bootstrapped = true;
      this._loadList();
    }
  }

  _mountClicks() {
    if (!this.el) return;
    this.el.querySelectorAll('[data-row]').forEach((el) => {
      el.addEventListener('click', () => this._select(el.getAttribute('data-row')));
    });
    this.el.querySelector('[data-action="reload"]')?.addEventListener('click', () => this._loadList());
    this.el.querySelector('[data-action="update"]')?.addEventListener('click', () => hardReload());
    this.el.querySelector('[data-action="help"]')?.addEventListener('click', () => this.setState({ helpOpen: !this.state.helpOpen }));
    this.el.querySelector('[data-action="close-help"]')?.addEventListener('click', () => this.setState({ helpOpen: false }));
  }
}

// Force the PWA to pick up the newest deploy. Unregisters every active
// service worker, deletes every Cache Storage entry, then hard-reloads the
// page so the next request hits the network and pulls fresh index.html +
// hashed asset URLs. Best-effort: any step that throws is logged and we still
// fall back to a plain reload.
async function hardReload() {
  try {
    if (typeof navigator !== 'undefined' && navigator.serviceWorker?.getRegistrations) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister().catch(() => {})));
    }
    if (typeof caches !== 'undefined' && caches.keys) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k).catch(() => {})));
    }
  } catch (_) { /* ignore — reload still runs */ }
  if (typeof window !== 'undefined') window.location.reload();
}

const STYLE = `<style>
.ocrlog-root{--base:#1e1e2e;--mantle:#181825;--crust:#11111b;--surface0:#313244;--surface1:#45475a;--overlay0:#6c7086;--text:#cdd6f4;--subtext0:#a6adc8;--blue:#89b4fa;--mauve:#cba6f7;--green:#a6e3a1;--yellow:#f9e2af;--red:#f38ba8;--peach:#fab387;--teal:#94e2d5;background:var(--base);color:var(--text);font-family:'JetBrains Mono','Fira Code','SF Mono',Consolas,monospace;font-size:13px;line-height:1.5;min-height:calc(100vh - 80px);border-radius:8px;border:1px solid var(--surface0);overflow:hidden;display:flex;flex-direction:column}
.ocrlog-root *{box-sizing:border-box}
.ocrlog-bar{display:flex;align-items:center;gap:.75rem;padding:.5rem .9rem;background:var(--mantle);border-bottom:1px solid var(--surface0);color:var(--subtext0)}
.ocrlog-bar .prompt{color:var(--green)}.ocrlog-bar .path{color:var(--blue)}.ocrlog-bar .cmd{color:var(--text)}
.ocrlog-bar .grow{flex:1}.ocrlog-bar button{background:transparent;color:var(--subtext0);border:1px solid var(--surface1);border-radius:4px;padding:.2rem .6rem;font:inherit;cursor:pointer}
.ocrlog-bar button:hover{color:var(--text);border-color:var(--overlay0)}
.ocrlog-body{display:grid;grid-template-columns:minmax(320px,38%) 1fr;flex:1;min-height:0}
.ocrlog-list{border-right:1px solid var(--surface0);overflow:auto;background:var(--mantle)}
.ocrlog-row{padding:.45rem .8rem;border-bottom:1px solid var(--surface0);cursor:pointer;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;color:var(--subtext0)}
.ocrlog-row .ts{color:var(--overlay0);margin-right:.5rem}
.ocrlog-row .src{color:var(--peach);margin-right:.5rem}.ocrlog-row .src.upload{color:var(--mauve)}
.ocrlog-row .dt{color:var(--text)}.ocrlog-row .dt.other{color:var(--yellow)}.ocrlog-row .err{color:var(--red)}
.ocrlog-row .dur{color:var(--overlay0);margin-left:.5rem;font-size:11.5px}
.ocrlog-row.is-selected{background:var(--surface0);color:var(--text);border-left:3px solid var(--blue);padding-left:calc(.8rem - 3px)}
.ocrlog-detail{padding:.9rem 1rem;overflow:auto;display:flex;flex-direction:column;gap:.9rem}
.ocrlog-detail h2{margin:0;font-size:13px;color:var(--mauve);font-weight:600}
.ocrlog-meta{display:grid;grid-template-columns:auto 1fr;gap:.15rem .9rem;color:var(--subtext0)}
.ocrlog-meta dt{color:var(--overlay0)}.ocrlog-meta dd{margin:0;color:var(--text);word-break:break-all}
.ocrlog-meta dd.err{color:var(--red)}.ocrlog-meta dd.other{color:var(--yellow)}.ocrlog-meta dd.deed{color:var(--green)}
.ocrlog-image{background:var(--crust);border:1px solid var(--surface0);border-radius:4px;padding:.4rem;display:flex;justify-content:center}
.ocrlog-image img{max-width:100%;max-height:480px;border-radius:2px;background:#fff}
.ocrlog-pdf{background:var(--crust);border:1px solid var(--surface0);border-radius:4px;padding:.4rem;display:flex;flex-direction:column;gap:.5rem}
.ocrlog-pdf object{width:100%;height:60vh;min-height:320px;border:0;background:#fff;border-radius:2px;display:block}
.ocrlog-pdf .open{align-self:flex-start;background:var(--surface0);color:var(--blue);font-size:12px;text-decoration:none;padding:.35rem .7rem;border-radius:4px;border:1px solid var(--surface1)}
.ocrlog-pdf .open:hover{background:var(--surface1);text-decoration:underline}
.ocrlog-pdf .fallback{padding:1.2rem;text-align:center;color:var(--subtext0);background:var(--mantle);border-radius:2px}
.ocrlog-pdf .fallback a{color:var(--blue);text-decoration:none}
.ocrlog-pdf .fallback a:hover{text-decoration:underline}
.ocrlog-filelink{background:var(--crust);border:1px solid var(--surface0);border-radius:4px;padding:.6rem .8rem;color:var(--blue);text-decoration:none;display:inline-block}
.ocrlog-filelink:hover{text-decoration:underline}
.ocrlog-pre{background:var(--crust);border:1px solid var(--surface0);border-radius:4px;padding:.6rem .8rem;margin:0;color:var(--text);max-height:60vh;overflow:auto;white-space:pre-wrap;word-break:break-word;font-size:12.5px}
.ocrlog-pre .k{color:var(--blue)}.ocrlog-pre .s{color:var(--green)}.ocrlog-pre .n{color:var(--peach)}.ocrlog-pre .b{color:var(--mauve)}
.ocrlog-empty{padding:2rem;color:var(--overlay0);text-align:center}
.ocrlog-foot{padding:.4rem .9rem;background:var(--mantle);border-top:1px solid var(--surface0);color:var(--overlay0);display:flex;gap:1rem;flex-wrap:wrap;font-size:12px}
.ocrlog-foot kbd{background:var(--surface0);color:var(--text);border-radius:3px;padding:0 .35rem;font-family:inherit;font-size:11.5px}
.ocrlog-help{position:absolute;inset:0;background:rgba(17,17,27,.86);display:flex;align-items:center;justify-content:center;padding:2rem}
.ocrlog-help-card{background:var(--mantle);border:1px solid var(--surface1);border-radius:6px;padding:1rem 1.2rem;min-width:320px;color:var(--text)}
.ocrlog-help-card h3{margin:.1rem 0 .6rem;color:var(--mauve)}
.ocrlog-help-card table{border-collapse:collapse;width:100%}.ocrlog-help-card td{padding:.15rem .5rem;color:var(--subtext0)}.ocrlog-help-card td kbd{background:var(--surface0);color:var(--text);border-radius:3px;padding:0 .35rem;font-family:inherit}
.ocrlog-bar .ver{color:var(--overlay0);font-size:11.5px;white-space:nowrap}
.ocrlog-bar .ver .v{color:var(--teal)}
@media (max-width:840px){
.ocrlog-root{min-height:calc(100vh - 60px);font-size:12.5px}
.ocrlog-body{grid-template-columns:1fr;grid-template-rows:auto 1fr}
.ocrlog-list{max-height:35vh;border-right:0;border-bottom:1px solid var(--surface0)}
.ocrlog-detail{padding:.7rem .8rem;gap:.7rem}
.ocrlog-image img{max-height:60vh}
.ocrlog-pdf object{height:55vh;min-height:260px}
.ocrlog-pre{max-height:50vh;font-size:12px}
.ocrlog-bar{flex-wrap:wrap;gap:.4rem;padding:.4rem .6rem}
.ocrlog-bar .cmd{font-size:11.5px}
.ocrlog-bar .ver{font-size:11px}
.ocrlog-foot{gap:.6rem;font-size:11px;padding:.35rem .6rem}
}
</style>`;


// --- Render helpers ---------------------------------------------------------

OcrLogsView.prototype._headerHtml = function () {
  const count = this.state.rows.length;
  const sel = this.state.rows.findIndex((r) => r.id === this.state.selectedId);
  const pos = sel >= 0 ? `${sel + 1}/${count}` : `0/${count}`;
  return `<div class="ocrlog-bar">
    <span class="prompt">▌</span>
    <span class="path">~/ocr-logs</span>
    <span class="cmd">$ list --mine --limit=100</span>
    <span class="grow"></span>
    <span class="ver" title="${esc(buildLabel())}">nestly <span class="v">${esc(versionTag())}</span></span>
    <span class="cmd">${esc(pos)}</span>
    <button data-action="reload" title="r — refetch list">reload</button>
    <button data-action="update" title="force PWA update + hard reload">update</button>
    <button data-action="help" title="?">?</button>
  </div>`;
};

OcrLogsView.prototype._listHtml = function () {
  if (this.state.loading) {
    return `<div class="ocrlog-list"><div class="ocrlog-empty">loading…</div></div>`;
  }
  if (this.state.error) {
    return `<div class="ocrlog-list"><div class="ocrlog-empty" style="color:var(--red)">${esc(this.state.error)}</div></div>`;
  }
  if (this.state.rows.length === 0) {
    return `<div class="ocrlog-list"><div class="ocrlog-empty">No OCR runs yet. Upload a title deed via /properties/new.</div></div>`;
  }
  const rows = this.state.rows.map((r) => {
    const isSel = r.id === this.state.selectedId;
    const ts = formatTs(r.created_at);
    const dt = r.document_type || (r.error_message ? 'ERR' : '?');
    const dtClass = r.error_message ? 'err' : (dt === 'title_deed' ? '' : (dt === '?' ? '' : 'other'));
    const srcClass = r.source === 'upload' ? 'src upload' : 'src';
    const dur = r.duration_ms != null ? msString(r.duration_ms) : '';
    return `<div class="ocrlog-row${isSel ? ' is-selected' : ''}" data-row="${esc(r.id)}">
      <span class="ts">${esc(ts)}</span>
      <span class="${srcClass}">${esc((r.source || '?').padEnd(7))}</span>
      <span class="dt ${dtClass}">${esc(dt)}</span>
      <span class="dur">${esc(dur)}</span>
    </div>`;
  }).join('');
  return `<div class="ocrlog-list">${rows}</div>`;
};

OcrLogsView.prototype._detailHtml = function () {
  const d = this.state.detail;
  if (this.state.detailLoading) return `<div class="ocrlog-detail"><div class="ocrlog-empty">loading row…</div></div>`;
  if (!d) return `<div class="ocrlog-detail"><div class="ocrlog-empty">Select a row (↑/↓ or j/k). Press ? for help.</div></div>`;
  const dt = d.document_type || (d.error_message ? 'ERR' : '?');
  const dtClass = d.error_message ? 'err' : (dt === 'title_deed' ? 'deed' : 'other');
  const meta = [
    ['id',              d.id],
    ['created_at',      d.created_at],
    ['source',          d.source],
    ['kind',            d.kind],
    ['document_type',   `<span class="${dtClass}">${esc(String(dt))}</span>`, true],
    ['model',           d.model || '—'],
    ['duration',        durationString(d)],
    ['ttfb',            msString(d.ttfb_ms)],
    ['tokens',          tokenString(d)],
    ['reasoning_tokens', d.reasoning_tokens == null ? '—' : String(d.reasoning_tokens)],
    ['request_bytes',   d.request_bytes == null ? '—' : String(d.request_bytes)],
    ['size_bytes',      d.size_bytes],
    ['mime',            d.mime],
    ['storage_path',    d.storage_path],
    ['property_id',     d.property_id || '—'],
    ['document_id',     d.document_id || '—'],
    ['ip_address',      d.ip_address || '—'],
    ['user_agent',      d.user_agent || '—'],
    ['error_message',   d.error_message ? `<span class="err">${esc(String(d.error_message))}</span>` : '—', true],
  ];
  const metaHtml = meta.map(([k, v, raw]) => `<dt>${esc(k)}</dt><dd${k === 'error_message' && d.error_message ? ' class="err"' : ''}>${raw ? v : esc(String(v ?? ''))}</dd>`).join('');
  const preview = previewHtml(d);
  return `<div class="ocrlog-detail">
    <h2>raw_response</h2>
    <pre class="ocrlog-pre">${prettyRaw(d.raw_response)}</pre>
    <h2>${previewHeading(d)}</h2>
    ${preview}
    <h2>metadata</h2>
    <dl class="ocrlog-meta">${metaHtml}</dl>
    <h2>extracted_fields (parsed)</h2>
    <pre class="ocrlog-pre">${d.extracted_fields ? prettyJson(d.extracted_fields) : esc('null')}</pre>
  </div>`;
};

OcrLogsView.prototype._footerHtml = function () {
  return `<div class="ocrlog-foot">
    <span><kbd>j</kbd>/<kbd>k</kbd> move</span>
    <span><kbd>g</kbd>/<kbd>G</kbd> first/last</span>
    <span><kbd>r</kbd> reload</span>
    <span><kbd>Enter</kbd> reload row</span>
    <span><kbd>?</kbd> help</span>
    <span><kbd>Esc</kbd> close</span>
  </div>`;
};

OcrLogsView.prototype._helpHtml = function () {
  return `<div class="ocrlog-help">
    <div class="ocrlog-help-card">
      <h3>OCR log — keyboard</h3>
      <table>
        <tr><td><kbd>j</kbd> / <kbd>↓</kbd></td><td>next row</td></tr>
        <tr><td><kbd>k</kbd> / <kbd>↑</kbd></td><td>previous row</td></tr>
        <tr><td><kbd>g</kbd> / <kbd>Home</kbd></td><td>first row</td></tr>
        <tr><td><kbd>G</kbd> / <kbd>End</kbd></td><td>last row</td></tr>
        <tr><td><kbd>Enter</kbd></td><td>reload current detail</td></tr>
        <tr><td><kbd>r</kbd></td><td>refetch list (fast, in-page)</td></tr>
        <tr><td>update</td><td>force PWA to fetch newest deploy + hard reload</td></tr>
        <tr><td><kbd>?</kbd></td><td>toggle this help</td></tr>
        <tr><td><kbd>Esc</kbd></td><td>close help</td></tr>
      </table>
      <div style="margin-top:.6rem;text-align:right"><button data-action="close-help" style="background:transparent;color:var(--subtext0);border:1px solid var(--surface1);border-radius:4px;padding:.2rem .8rem;font:inherit;cursor:pointer">close</button></div>
    </div>
  </div>`;
};

// Build-time release metadata injected by Vite (see vite.config.js / vitest.config.js).
function versionTag() {
  // eslint-disable-next-line no-undef
  return `v${__APP_VERSION__} (${__APP_COMMIT__})`;
}
function buildLabel() {
  // eslint-disable-next-line no-undef
  return `built ${__APP_BUILD_TIME__}`;
}

function formatTs(ts) {
  if (!ts) return '';
  // The server emits 'Y-m-d H:i:s.u'. Show 'MM-DD HH:MM:SS' to keep the
  // list line tight.
  const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})/.exec(String(ts));
  return m ? `${m[2]}-${m[3]} ${m[4]}:${m[5]}:${m[6]}` : String(ts);
}

function tokenString(d) {
  const t = d.total_tokens, p = d.prompt_tokens, c = d.completion_tokens;
  if (t == null && p == null && c == null) return '—';
  return `${t ?? '?'} (prompt ${p ?? '?'} / completion ${c ?? '?'})`;
}

// Format a millisecond count as either "812 ms" or "12.3 s" so an admin can
// scan latency at a glance — the OCR rows we care about run multi-second.
function msString(ms) {
  if (ms == null || ms === '') return '—';
  const n = Number(ms);
  if (!Number.isFinite(n)) return '—';
  if (n < 1000) return `${n} ms`;
  return `${(n / 1000).toFixed(n < 10000 ? 2 : 1)} s`;
}

// duration_ms is the headline number ("how long did the call take?"). Append
// the network breakdown (ttfb_ms / total minus ttfb ~= response-streaming time)
// so the user can tell whether the bottleneck was model think-time or the
// network. Falls through to a single value when only one number is present.
function durationString(d) {
  const total = d.duration_ms;
  const ttfb = d.ttfb_ms;
  if (total == null && ttfb == null) return '—';
  if (total != null && ttfb != null) {
    const thinkAndUpload = msString(ttfb);
    const stream = Math.max(0, Number(total) - Number(ttfb));
    return `${msString(total)}  (before first byte ${thinkAndUpload} · streaming ${msString(stream)})`;
  }
  return msString(total ?? ttfb);
}

function prettyRaw(raw) {
  if (raw == null || raw === '') return esc('(empty)');
  // Try to pretty-print as JSON; fall back to plain escape.
  try {
    const parsed = JSON.parse(String(raw));
    return prettyJson(parsed);
  } catch (_) {
    return esc(String(raw));
  }
}

function previewHeading(d) {
  if (!d.image_url) return 'document (signed URL, 30 min)';
  const mime = String(d.mime || '').toLowerCase();
  if (mime === 'application/pdf') return 'document (PDF, signed URL, 30 min)';
  if (mime.startsWith('image/')) return 'image (signed URL, 30 min)';
  return 'document (signed URL, 30 min)';
}

function previewHtml(d) {
  if (!d.image_url) return `<div class="ocrlog-empty">image_url unavailable</div>`;
  const url = esc(d.image_url);
  const mime = String(d.mime || '').toLowerCase();
  if (mime === 'application/pdf') {
    // <object> over <iframe> because:
    //   1. mobile Safari/Chrome refuse to render cross-origin PDFs in an
    //      iframe, leaving a blank/error box — <object> lets us provide
    //      fallback HTML the browser renders when the plugin can't display.
    //   2. the 'open in new tab' link is the always-working escape hatch and
    //      we surface it BEFORE the embed so the user never has to scroll
    //      past a broken viewer to find it.
    return `<div class="ocrlog-pdf">
      <a class="open" href="${url}" target="_blank" rel="noopener">open PDF in new tab ↗</a>
      <object data="${url}" type="application/pdf">
        <div class="fallback">
          Your browser can't preview this PDF inline.<br>
          <a href="${url}" target="_blank" rel="noopener">Open it in a new tab ↗</a>
        </div>
      </object>
    </div>`;
  }
  if (mime.startsWith('image/')) {
    return `<div class="ocrlog-image"><img src="${url}" alt="OCR input image" /></div>`;
  }
  return `<a class="ocrlog-filelink" href="${url}" target="_blank" rel="noopener">open document ↗</a>`;
}

function prettyJson(obj) {
  const text = JSON.stringify(obj, null, 2);
  // Tiny JSON syntax highlighter (key / string / number / boolean / null).
  return esc(text)
    .replace(/(&quot;[^&]*?&quot;)(\s*:\s*)/g, '<span class="k">$1</span>$2')
    .replace(/:\s*(&quot;[^&]*?&quot;)/g, (m, s) => m.replace(s, `<span class="s">${s}</span>`))
    .replace(/(:\s*)(-?\d+(?:\.\d+)?)/g, '$1<span class="n">$2</span>')
    .replace(/(:\s*)(true|false|null)\b/g, '$1<span class="b">$2</span>');
}
